import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Send, Shield, CheckCircle, Zap, Home } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const LeadForm = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", city: "", service: "" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Nome é obrigatório";
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) errs.email = "E-mail inválido";
    if (!form.phone.trim() || form.phone.replace(/\D/g, "").length < 10) errs.phone = "Telefone inválido";
    if (!form.city.trim()) errs.city = "Cidade é obrigatória";
    if (!form.service) errs.service = "Selecione um serviço";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const formatPhone = (val) => {
    const digits = val.replace(/\D/g, "").slice(0, 11);
    if (digits.length <= 2) return digits;
    if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  };

  const serviceLabels = {
    gd: "Energia por Assinatura",
    install: "Instalação de Painéis",
    both: "Ambos os serviços",
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.replace(/\D/g, ""),
        city: form.city.trim(),
        service: form.service,
      });
      setSubmitted(true);
      toast.success("Cadastro realizado! Entraremos em contato em breve.");
    } catch {
      toast.error("Erro ao enviar. Tente novamente.");
    }
    setLoading(false);
  };

  if (submitted) {
    return (
      <section id="contato" data-testid="lead-form-section" className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-lg mx-auto text-center"
          >
            <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" style={{ background: "rgba(34, 197, 94, 0.1)" }}>
              <CheckCircle className="w-10 h-10 text-emerald-500" />
            </div>
            <h3 className="text-2xl font-bold text-slate-900 mb-3" data-testid="lead-form-success">
              Pronto! Recebemos seus dados
            </h3>
            <p className="text-slate-500 mb-2">
              Serviço escolhido: <strong>{serviceLabels[form.service]}</strong>
            </p>
            <p className="text-slate-500 mb-6">
              Nossa equipe entrará em contato pelo WhatsApp para dar sequência. Fique de olho!
            </p>
            <Button
              data-testid="lead-form-whatsapp-btn"
              onClick={() => window.open(`https://wa.me/5531984297314?text=${encodeURIComponent(`Olá! Me cadastrei no site. Tenho interesse em: ${serviceLabels[form.service]}. Meu nome é ${form.name}.`)}`, "_blank")}
              className="rounded-full bg-[#25D366] hover:bg-[#20bd5a] text-white h-12 px-8 font-semibold"
            >
              Falar no WhatsApp agora
            </Button>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section id="contato" data-testid="lead-form-section" className="py-20 md:py-28 bg-white relative overflow-hidden">
      <div className="absolute top-0 right-0 w-96 h-96 rounded-full opacity-5" style={{ background: "#1a4fa0", filter: "blur(100px)" }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider bg-[#1a4fa0]/10 text-[#1a4fa0] mb-4">
              Comece Agora
            </span>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-4">
              Pronto para <span style={{ color: "#1a4fa0" }}>economizar</span>?
            </h2>
            <p className="text-base md:text-lg text-slate-500 leading-relaxed mb-8">
              Preencha o formulário com seus dados e escolha o serviço que mais combina com você. 
              Nossa equipe entrará em contato rapidamente. Sem compromisso.
            </p>

            <div className="space-y-4">
              {[
                "Atendimento rápido via WhatsApp",
                "Orçamento sem compromisso",
                "Consultoria personalizada gratuita",
                "Suporte do início ao fim",
              ].map((item) => (
                <div key={item} className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "rgba(34, 197, 94, 0.15)" }}>
                    <CheckCircle className="w-3.5 h-3.5 text-emerald-600" />
                  </div>
                  <span className="text-sm text-slate-600">{item}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Right - form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Card className="border-0 shadow-2xl shadow-[#1a4fa0]/10 rounded-3xl">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="lead-form">
                  {/* Service Selection */}
                  <div>
                    <label className="text-sm font-semibold text-slate-700 mb-2 block">Qual serviço te interessa?</label>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      {[
                        { value: "gd", label: "Assinatura", icon: Zap, desc: "Sem obras" },
                        { value: "install", label: "Painéis", icon: Home, desc: "Na sua casa" },
                        { value: "both", label: "Ambos", icon: CheckCircle, desc: "Quero saber" },
                      ].map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          data-testid={`service-option-${opt.value}`}
                          onClick={() => { setForm({ ...form, service: opt.value }); setErrors({ ...errors, service: undefined }); }}
                          className={`flex flex-col items-center gap-1 p-3 rounded-xl border-2 transition-all duration-200 text-center ${
                            form.service === opt.value
                              ? "border-[#1a4fa0] bg-[#1a4fa0]/5"
                              : "border-slate-200 hover:border-slate-300"
                          }`}
                        >
                          <opt.icon className={`w-5 h-5 ${form.service === opt.value ? "text-[#1a4fa0]" : "text-slate-400"}`} />
                          <span className={`text-xs font-semibold ${form.service === opt.value ? "text-[#1a4fa0]" : "text-slate-600"}`}>{opt.label}</span>
                          <span className="text-[10px] text-slate-400">{opt.desc}</span>
                        </button>
                      ))}
                    </div>
                    {errors.service && <p className="text-xs text-red-500 mt-1">{errors.service}</p>}
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Nome completo</label>
                    <Input
                      data-testid="lead-form-name"
                      placeholder="Seu nome"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className={`h-12 rounded-xl border-slate-200 focus:ring-2 focus:ring-[#1a4fa0]/20 focus:border-[#1a4fa0] ${errors.name ? "border-red-400" : ""}`}
                    />
                    {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-slate-700 mb-1.5 block">E-mail</label>
                    <Input
                      data-testid="lead-form-email"
                      type="email"
                      placeholder="seu@email.com"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className={`h-12 rounded-xl border-slate-200 focus:ring-2 focus:ring-[#1a4fa0]/20 focus:border-[#1a4fa0] ${errors.email ? "border-red-400" : ""}`}
                    />
                    {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Telefone/WhatsApp</label>
                    <Input
                      data-testid="lead-form-phone"
                      placeholder="(31) 99999-9999"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: formatPhone(e.target.value) })}
                      className={`h-12 rounded-xl border-slate-200 focus:ring-2 focus:ring-[#1a4fa0]/20 focus:border-[#1a4fa0] ${errors.phone ? "border-red-400" : ""}`}
                    />
                    {errors.phone && <p className="text-xs text-red-500 mt-1">{errors.phone}</p>}
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Cidade</label>
                    <Input
                      data-testid="lead-form-city"
                      placeholder="Sua cidade"
                      value={form.city}
                      onChange={(e) => setForm({ ...form, city: e.target.value })}
                      className={`h-12 rounded-xl border-slate-200 focus:ring-2 focus:ring-[#1a4fa0]/20 focus:border-[#1a4fa0] ${errors.city ? "border-red-400" : ""}`}
                    />
                    {errors.city && <p className="text-xs text-red-500 mt-1">{errors.city}</p>}
                  </div>

                  <Button
                    data-testid="lead-form-submit"
                    type="submit"
                    disabled={loading}
                    className="w-full rounded-full bg-[#1a4fa0] hover:bg-[#153e80] text-white h-14 text-base font-bold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-transform duration-200"
                  >
                    {loading ? "Enviando..." : "Quero Economizar"}
                    <Send className="w-4 h-4 ml-2" />
                  </Button>

                  <div className="flex items-center gap-2 justify-center pt-2">
                    <Shield className="w-3.5 h-3.5 text-slate-400" />
                    <p className="text-xs text-slate-400">Seus dados estão seguros conosco.</p>
                  </div>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
